import React from "react"
import { Navigate, useParams } from "react-router-dom"
import { useOrganizationPermissions } from "@/hooks/useOrganizationPermissions"
import Loader from "@/components/Loader"
import ErrorMessage from "@/components/ErrorMessage"

// Component to show when user doesn't have access
const NoAccessPage = () => (
  <div className="flex flex-col items-center justify-center p-8 text-center h-full">
    <ErrorMessage message="Access Denied">
      <p className="mt-2">You don't have permission to access this page.</p>
    </ErrorMessage>
  </div>
)

/**
 * Higher-order component that checks if a user has permission to access a page
 * @param Component The component to render if the user has access
 * @param pageName The name of the page to check permissions for (should match the route)
 * @returns A wrapped component that includes permission checks
 */
export const withPagePermission = (
  Component: React.ComponentType<any>,
  pageName: string,
) => {
  // Create a wrapper component
  const WithPagePermission = (props: any) => {
    const { id: organizationId } = useParams<{ id: string }>()
    const { hasPageAccess, isLoading, hasEditPermissionForPage } =
      useOrganizationPermissions({ page: pageName })

    // Show loader while permissions are being checked
    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-full">
          <Loader />
        </div>
      )
    }

    // If user doesn't have access, show access denied page
    if (!hasPageAccess) {
      // For organization-specific pages, redirect to the organizations list
      if (organizationId) {
        return <Navigate to="/organizations" replace />
      }

      // Otherwise show the access denied page
      return <NoAccessPage />
    }

    // Pass down the hasEditPermission flag to the wrapped component
    // This will allow components to conditionally render edit controls
    const hasEditPermission = hasEditPermissionForPage(pageName)

    // Render the wrapped component if the user has access
    return <Component {...props} hasEditPermission={hasEditPermission} />
  }

  // Set display name for debugging
  WithPagePermission.displayName = `WithPagePermission(${
    Component.displayName || Component.name || "Component"
  })`

  return WithPagePermission
}

export default withPagePermission
